import { ArrowRight, CoatHanger } from "@phosphor-icons/react"
import { max, min, sumBy } from "lodash"
import CollectionStatus from "~/components/collections/CollectionStatus"
import { CardTitle } from "~/components/reusable/Card"
import {
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
} from "~/components/design-system/components"
import InformationGrid from "~/components/reusable/InformationGrid"
import InformationRow from "~/components/reusable/InformationRow"
import { UserAvatar } from "~/components/reusable/users/UserAvatar"
import { DemandPlanEventType } from "~/constants/demandPlanEventType"
import { type ICollectionDetails } from "~/hooks/useCollections"
import { useGetDemandPlanEvents } from "~/hooks/useDemandPlan"
import { useUserLookup } from "~/hooks/useUsers"
import { SYSTEM_ADMIN_NAME, isHakioEmail } from "~/utils/users"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { type ICollectionGroup } from "~/hooks/useCollections"
import { calculateCollectionStatus } from "~/utils/collections"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { selectBestImageUrlFromObjects } from "~/utils/imageUtils"
import { useInspectorExpandKey } from "~/components/design-system/pages"

type CollectionInfoCardProps = {
  collections: ICollectionDetails[]
  collectionGroup?: ICollectionGroup
  variants: IVariantSummary[]
  activeVariantsCount?: number
  defaultExpanded?: boolean
  demandPlanId?: string
}

function resolvePublisher(userId: string, lookup: ReturnType<typeof useUserLookup>) {
  const email = lookup.getUserEmail(userId)
  if (isHakioEmail(email)) return { name: SYSTEM_ADMIN_NAME, email: "", picture: "" }
  return { name: lookup.getUserName(userId), email, picture: lookup.getUserImage(userId) }
}

const CollectionInfoCard = ({
  collections,
  collectionGroup,
  variants,
  activeVariantsCount,
  defaultExpanded = true,
  demandPlanId,
}: CollectionInfoCardProps) => {
  const persistKey = useInspectorExpandKey("collection-info")
  const { data: demandPlanEvents } = useGetDemandPlanEvents(demandPlanId ?? "", !!demandPlanId)
  const userLookup = useUserLookup()
  const firstCollection = collections[0]
  if (!firstCollection) return null

  // getEvents returns events ordered by timeStamp desc, so find() gives the latest publish
  const lastPublishEvent = demandPlanEvents?.find(
    (event) => event.description === DemandPlanEventType.PublishedDemandPlan,
  )
  const publisher = lastPublishEvent?.userId
    ? resolvePublisher(lastPublishEvent.userId, userLookup)
    : null

  const totalVariantsCount = variants.length
  const resolvedActiveCount = activeVariantsCount ?? totalVariantsCount
  const startDate = min(collections.map((c) => c.startDate))
  const endDate = max(collections.map((c) => c.endDate))
  const rawStartDate = min(collections.map((c) => c.rawStartDate))
  const rawEndDate = max(collections.map((c) => c.rawEndDate))
  const preorderPeriodStart = min(collections.map((c) => c.preOrderPeriodStart))
  const preorderPeriodEnd = max(collections.map((c) => c.preOrderPeriodEnd))
  const status = calculateCollectionStatus(startDate ?? null, endDate ?? null)
  const name = collectionGroup?.name ?? firstCollection.name
  const imageUrl = selectBestImageUrlFromObjects(variants)

  const periodStart = rawStartDate ?? startDate
  const periodEnd = rawEndDate ?? endDate

  return (
    <ExpandableCard persistKey={persistKey} defaultExpanded={defaultExpanded} className="shrink-0">
      <ExpandableCardHeader>
        <CardTitle>Collection details</CardTitle>
      </ExpandableCardHeader>
      <ExpandableCardContent>
        <InformationGrid className="gap-y-3">
          <InformationRow title="Collection" valueImage={imageUrl ?? ""} value={name} noTruncate />

          <InformationRow title="Status" valueComponent={<CollectionStatus status={status} />} />

          {collections.length > 1 && (
            <InformationRow title="Sub-collections" value={String(collections.length)} />
          )}

          <InformationRow
            title="Variants"
            valueIcon={<CoatHanger className="w-4 h-4" />}
            value={formatNumber(resolvedActiveCount)}
          />

          {resolvedActiveCount < totalVariantsCount && (
            <InformationRow
              title="Out variants"
              value={formatNumber(totalVariantsCount - resolvedActiveCount, 0)}
            />
          )}

          <InformationRow
            title="SKUs"
            valueIcon={<CoatHanger className="w-4 h-4" />}
            value={formatNumber(sumBy(collections, "skuCount"), 0)}
          />

          {periodStart && periodEnd && (
            <InformationRow
              title="Period"
              valueComponent={
                <div className="flex items-center gap-2">
                  <span>{formatDate(periodStart, DateFormatType.FullDateShort)}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-fg-muted shrink-0" />
                  <span>{formatDate(periodEnd, DateFormatType.FullDateShort)}</span>
                </div>
              }
            />
          )}

          {preorderPeriodStart && preorderPeriodEnd && (
            <InformationRow
              // eslint-disable-next-line @hakio/no-untranslated-terminology
              title="Pre-order period"
              valueComponent={
                <div className="flex items-center gap-2">
                  <span>{formatDate(preorderPeriodStart, DateFormatType.FullDateShort)}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-fg-muted shrink-0" />
                  <span>{formatDate(preorderPeriodEnd, DateFormatType.FullDateShort)}</span>
                </div>
              }
            />
          )}

          {demandPlanId && lastPublishEvent && (
            <InformationRow
              title="Last published"
              value={
                lastPublishEvent
                  ? formatDate(lastPublishEvent.timeStamp, DateFormatType.PointInTime)
                  : "-"
              }
            />
          )}

          {publisher && (
            <InformationRow
              title="Published by"
              valueAvatar={
                <UserAvatar
                  name={publisher.name}
                  email={publisher.email}
                  picture={publisher.picture}
                  size={20}
                />
              }
              value={publisher.name}
            />
          )}
        </InformationGrid>
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

export default CollectionInfoCard
